import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Activity, AlertTriangle, CheckCircle2, Clock, Mail } from "lucide-react";

interface Pipeline {
  source: string;
  label: string;
  status: "healthy" | "stale" | "failing" | "never_ran";
  lastRunAt: string | null;
  ageHours: number | null;
  itemsSeen: number;
  itemsInserted: number;
  itemsUpdated: number;
  itemsFailed: number;
  error: string | null;
}

interface PipelinesPayload {
  pipelines: Pipeline[];
  email24h: { total: number; failed: number; failureRate: number };
}

function formatAge(hours: number | null) {
  if (hours === null) return "—";
  if (hours < 1) return `${Math.round(hours * 60)}m ago`;
  if (hours < 48) return `${Math.round(hours)}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function StatusDot({ status }: { status: Pipeline["status"] }) {
  const tone = {
    healthy: "bg-emerald-500",
    stale: "bg-amber-500",
    failing: "bg-destructive",
    never_ran: "bg-muted-foreground",
  }[status];
  return <span className={`h-2.5 w-2.5 rounded-full ${tone} shrink-0`} aria-label={status} />;
}

function PipelineCard({ p }: { p: Pipeline }) {
  const Icon = p.status === "healthy" ? CheckCircle2 : p.status === "failing" ? AlertTriangle : Clock;
  const iconTone = {
    healthy: "text-emerald-500",
    stale: "text-amber-500",
    failing: "text-destructive",
    never_ran: "text-muted-foreground",
  }[p.status];
  return (
    <div className="bg-card border border-border rounded-[0.875rem] p-4">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <StatusDot status={p.status} />
          <span className="text-sm font-medium truncate">{p.label}</span>
        </div>
        <Icon className={`h-4 w-4 shrink-0 ${iconTone}`} />
      </div>
      <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
        <span>Last run: <span className="text-foreground">{formatAge(p.ageHours)}</span></span>
        <span>Seen: <span className="text-foreground">{p.itemsSeen}</span></span>
        <span>+{p.itemsInserted} ins</span>
        <span>·{p.itemsUpdated} upd</span>
        {p.itemsFailed > 0 && <span className="text-destructive">·{p.itemsFailed} failed</span>}
      </div>
      {p.error && (
        <div className="text-xs text-destructive mt-2 line-clamp-2">{p.error}</div>
      )}
    </div>
  );
}

export default function PipelinesStrip() {
  const { data, isLoading } = useQuery<PipelinesPayload>({
    queryKey: ["admin-pipelines-overview"],
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("admin-dashboard-metrics", {
        body: { action: "pipelines_overview" },
      });
      if (error) throw new Error(error.message);
      if ((data as any)?.error) throw new Error((data as any).error);
      return data as PipelinesPayload;
    },
  });

  const failureRate = data?.email24h.failureRate ?? 0;
  const emailStatus: Pipeline["status"] =
    !data || data.email24h.total === 0
      ? "never_ran"
      : failureRate > 0.1
        ? "failing"
        : failureRate > 0.02
          ? "stale"
          : "healthy";

  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        <Activity className="h-4 w-4 text-primary" />
        <h2 className="font-display text-lg font-semibold">Data pipelines</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {isLoading && (
          <div className="md:col-span-3 text-sm text-muted-foreground bg-card border border-border rounded-[0.875rem] p-4">
            Loading pipeline health…
          </div>
        )}
        {data?.pipelines.map((p) => <PipelineCard key={p.source} p={p} />)}
        {data && (
          <div className="bg-card border border-border rounded-[0.875rem] p-4">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex items-center gap-2 min-w-0">
                <StatusDot status={emailStatus} />
                <span className="text-sm font-medium truncate">Email queue (24h)</span>
              </div>
              <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
            </div>
            <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
              <span>Sent: <span className="text-foreground">{data.email24h.total - data.email24h.failed}</span></span>
              {data.email24h.failed > 0 && (
                <span className="text-destructive">Failed: {data.email24h.failed}</span>
              )}
              <span>Failure rate: <span className="text-foreground">{(failureRate * 100).toFixed(1)}%</span></span>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}